import { useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { AvailableShiftModel } from 'api/ShiftWizardApi';
import { getLoanGoal } from 'selectors/getLoanGoal';
import { selectAvailableShifts } from 'selectors/getAvailableShifts';
import { AICoachGoalType, AICoachGoalPriority } from 'enums/AICoach';
import { AICoachGoalItem } from 'api/LoanOfferApi';
import { getAvailableShifts } from 'thunks';
import { getApplicationData } from 'selectors/getApplicationData';
import { toShiftDateKeyFromIso } from 'utils/shiftDateUtils';

export interface AvailableShift {
  id: string;
  shiftId: number;
  shiftRequirementId: number;
  date: string;
  shiftStartDate: string;
  shiftEndDate: string;
  timeRange: string;
  department: string;
  location: string;
  team: string;
  position: string;
  shiftType: string;
  staffRequired: number;
  staffHired: number;
  shiftRequirementFilled: boolean;
  isUsedForOpenShifts: boolean;
  showOpenShifts: boolean;
  goalAcceleration: string;
  amount?: number;
}

const getGoalTitle = (
  goalType: AICoachGoalType,
  description: string | undefined,
  goalAcceleration?: string,
): string => {
  const timeLabel = goalAcceleration ? ` ${goalAcceleration}` : '';

  switch (goalType) {
    case AICoachGoalType.PayDownCreditCardDebt:
    case AICoachGoalType.PayDownStudentLoanDebt:
      return `Pay off debt${timeLabel} faster`;
    case AICoachGoalType.BuildEmergencyFund:
      return `Save for emergencies${timeLabel} faster`;
    case AICoachGoalType.SaveForSpecificExpense:
      return `Save for ${description ?? 'your expense'}${timeLabel} faster`;
    case AICoachGoalType.ImproveCreditScore:
      return 'Increase your credit score with your extra income';
    case AICoachGoalType.SaveForRetirement:
      return `Save for retirement${timeLabel} faster`;
    case AICoachGoalType.Other:
      return `Save for ${description ?? 'your goal'}${timeLabel} faster`;
    default:
      return `Save for emergencies${timeLabel} faster`;
  }
};

const getGoalDescription = (): string => 'Pick an extra shift and achieve your goals faster.';

export interface UseAvailableShiftsResult {
  shifts: AvailableShift[];
  goalMessage: string;
  goalDescription: string;
  isLoading: boolean;
}

const formatTimeRange = (beginTime: string, endTime: string, shiftStartDate: string, shiftEndDate: string): string => {
  const startDay = toShiftDateKeyFromIso(shiftStartDate);
  const endDay = toShiftDateKeyFromIso(shiftEndDate);
  const endTimeDisplay = startDay !== endDay ? `${endTime}+1` : endTime;

  return `${beginTime} - ${endTimeDisplay}`;
};

const normalizeShiftDate = (shiftStartDate: string): string => toShiftDateKeyFromIso(shiftStartDate);

const mapShiftToAvailableShift = (shift: AvailableShiftModel): AvailableShift => ({
  id: shift.shiftRequirementId.toString(),
  shiftId: shift.shiftId,
  shiftRequirementId: shift.shiftRequirementId,
  date: normalizeShiftDate(shift.shiftStartDate),
  shiftStartDate: shift.shiftStartDate,
  shiftEndDate: shift.shiftEndDate,
  timeRange: formatTimeRange(shift.shiftBeginTime, shift.shiftEndTime, shift.shiftStartDate, shift.shiftEndDate),
  department: shift.departmentName,
  location: shift.locationName,
  team: shift.teamName,
  position: shift.position,
  shiftType: shift.shiftType,
  staffRequired: shift.staffRequired,
  staffHired: shift.staffHired,
  shiftRequirementFilled: shift.shiftRequirementFilled,
  isUsedForOpenShifts: shift.isUsedForOpenShifts,
  showOpenShifts: shift.showOpenShifts,
  goalAcceleration: shift.goalAcceleration,
  amount: shift.amount ?? undefined,
});

const findPrimaryGoal = (goals?: AICoachGoalItem[]): AICoachGoalItem | undefined =>
  goals?.find((g) => g.priority === AICoachGoalPriority.Primary);

const getNearestShiftGoalAcceleration = (shifts: AvailableShiftModel[]): string | undefined =>
  shifts.find((shift) => shift.showOpenShifts && shift.goalAcceleration)?.goalAcceleration;

export const useAvailableShifts = (): UseAvailableShiftsResult => {
  const dispatch = useDispatch();
  const { aiCoachGoals } = useSelector(getLoanGoal);
  const { application } = useSelector(getApplicationData);
  const { shifts, isLoading } = useSelector(selectAvailableShifts);

  const applicationId = application?.id;
  useEffect(() => {
    if (!applicationId) return;
    dispatch(getAvailableShifts(applicationId));
  }, [applicationId]);

  const mappedShifts: AvailableShift[] = useMemo(
    () => shifts.filter((shift) => shift.showOpenShifts).map(mapShiftToAvailableShift),
    [shifts],
  );

  const primaryGoal = useMemo(() => findPrimaryGoal(aiCoachGoals), [aiCoachGoals]);

  const goalMessage = useMemo(() => {
    const goalType = primaryGoal?.type ?? AICoachGoalType.BuildEmergencyFund;
    const goalAcceleration = getNearestShiftGoalAcceleration(shifts);

    return getGoalTitle(goalType, primaryGoal?.description, goalAcceleration);
  }, [primaryGoal, shifts]);

  const goalDescription = useMemo(() => getGoalDescription(), []);

  return {
    isLoading,
    shifts: mappedShifts,
    goalMessage,
    goalDescription,
  };
};
